import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { ResizableImage } from "./ResizableImage";
import { SpacerParagraph } from "./SpacerParagraph";
import { EmailOnly } from "./EmailOnly";
import { Markdown } from "tiptap-markdown";
import { Button, Input, Popover, Space, theme } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  MailOutlined,
  OrderedListOutlined,
  PictureOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { imageFilesFromClipboard } from "./pasteImageUtils";
import {
  collectImageSrcs,
  rehostForeignImages,
  uploadPastedImageFiles,
} from "./rehostPastedImages";
import styles from "./MarkdownEditor.module.css";

interface MarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  editable?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

function readMarkdown(editor: Editor): string {
  const storage = editor.storage.markdown as { getMarkdown: () => string };
  return storage.getMarkdown();
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  editable = true,
  onUploadingChange,
}) => {
  const { token } = theme.useToken();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlightRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);

  const runTracked = useCallback(async (task: Promise<void>) => {
    inFlightRef.current += 1;
    setBusy(true);
    try {
      await task;
    } finally {
      inFlightRef.current -= 1;
      if (inFlightRef.current === 0) setBusy(false);
    }
  }, []);

  useEffect(() => {
    onUploadingChange?.(busy);
  }, [busy, onUploadingChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ paragraph: false }),
      SpacerParagraph,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow" },
      }),
      ResizableImage,
      EmailOnly,
      Markdown.configure({ html: true }),
    ],
    content: value,
    editable,
    onUpdate: ({ editor }) => onChange(readMarkdown(editor)),
    editorProps: {
      handlePaste: (_view, event) => {
        const activeEditor = editorRef.current;
        if (!activeEditor) return false;
        const clipboard = event.clipboardData;
        const files = imageFilesFromClipboard(clipboard);
        const html = clipboard?.getData("text/html") ?? "";
        if (files.length > 0 && !html) {
          event.preventDefault();
          void runTracked(uploadPastedImageFiles(activeEditor, files));
          return true;
        }
        const knownSrcs = collectImageSrcs(activeEditor);
        window.setTimeout(() => {
          void runTracked(rehostForeignImages(activeEditor, knownSrcs));
        }, 0);
        return false;
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  const handleImageSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !editor) return;
    await runTracked(uploadPastedImageFiles(editor, [file]));
  };

  const openLinkPopover = (open: boolean) => {
    if (open && editor) {
      setLinkUrl((editor.getAttributes("link").href as string) ?? "");
    }
    setLinkOpen(open);
  };

  const applyLink = () => {
    if (!editor) return;
    const chain = editor.chain().focus().extendMarkRange("link");
    const url = linkUrl.trim();
    if (url) {
      chain.setLink({ href: url }).run();
    } else {
      chain.unsetLink().run();
    }
    setLinkOpen(false);
  };

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => {
    if (!editor) return;
    if (value !== readMarkdown(editor)) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadius,
      }}
    >
      {editable && (
        <Space
          size={2}
          style={{
            padding: token.paddingXS,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            position: "sticky",
            top: 0,
            zIndex: 1,
            background: token.colorBgContainer,
            borderTopLeftRadius: token.borderRadius,
            borderTopRightRadius: token.borderRadius,
          }}
        >
          <Button
            size="small"
            type={editor.isActive("bold") ? "primary" : "text"}
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <Button
            size="small"
            type={editor.isActive("italic") ? "primary" : "text"}
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <Popover
            open={linkOpen}
            onOpenChange={openLinkPopover}
            trigger="click"
            placement="bottom"
            content={
              <Space.Compact style={{ width: 280 }}>
                <Input
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onPressEnter={applyLink}
                  autoFocus
                />
                <Button type="primary" onClick={applyLink}>
                  {linkUrl.trim() ? "Apply" : "Remove"}
                </Button>
              </Space.Compact>
            }
          >
            <Button
              size="small"
              type={editor.isActive("link") ? "primary" : "text"}
              icon={<LinkOutlined />}
            />
          </Popover>
          <Button
            size="small"
            type={editor.isActive("heading", { level: 2 }) ? "primary" : "text"}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            H2
          </Button>
          <Button
            size="small"
            type={editor.isActive("heading", { level: 3 }) ? "primary" : "text"}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            H3
          </Button>
          <Button
            size="small"
            type={editor.isActive("bulletList") ? "primary" : "text"}
            icon={<UnorderedListOutlined />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <Button
            size="small"
            type={editor.isActive("orderedList") ? "primary" : "text"}
            icon={<OrderedListOutlined />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <Button
            size="small"
            type="text"
            icon={<PictureOutlined />}
            loading={busy}
            onClick={() => fileInputRef.current?.click()}
          />
          <Button
            size="small"
            type={editor.isActive("emailOnly") ? "primary" : "text"}
            icon={<MailOutlined />}
            title="Show the selection in the email only"
            onClick={() => editor.chain().focus().toggleWrap("emailOnly").run()}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: "none" }}
            onChange={handleImageSelected}
          />
        </Space>
      )}
      <div
        className={styles.editorArea}
        style={{ padding: token.padding }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default MarkdownEditor;
